import { 
  Scene, 
  Mesh, 
  Vector3, 
  Animation, 
  QuadraticEase, 
  EasingFunction,
  AbstractMesh
} from "babylonjs"

/**
 * Function to animate a list of meshes by scaling them from zero to their original scale with staggered timing
 * @param meshes - Array of Babylon.js meshes to animate
 * @param scene - The Babylon.js scene
 * @param baseDelay - Base delay before starting animation (in seconds)
 * @param staggerDelay - Delay between each mesh's animation start (in seconds)
 */
export function animateMeshesWithStagger(
  meshes: AbstractMesh[], 
  scene: Scene, 
  baseDelay: number = 0, 
  staggerDelay: number = 0.2
): void {
  // Store original scales and set meshes to zero scale
  const originalScales: Map<AbstractMesh, Vector3> = new Map();
  
  meshes.forEach(mesh => {
    // Store the original scale
    originalScales.set(mesh, mesh.scaling.clone());
    // Set to zero scale
    mesh.scaling = new Vector3(0, 0, 0);
  });
  
  // Create easing function (easeInOutQuad)
  const easing = new QuadraticEase();
  easing.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
  
  // Animate each mesh with a staggered delay
  meshes.forEach((mesh, index) => {
    // Randomize duration between 0.2 and 0.4 seconds
    const duration = 0.2 + Math.random() * 0.2;
    
    // Calculate staggered delay
    const delay = baseDelay + (index * staggerDelay);
    
    // Create animation for scaling
    const scaleAnim = new Animation(
      "scaleAnimation-" + mesh.name,
      "scaling",
      30, // frames per second
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    
    // Get the original scale for this mesh
    const originalScale = originalScales.get(mesh) || new Vector3(1, 1, 1);
    
    // Animation keys (from zero scale to original scale)
    const keyframes = [
      {
        frame: 0,
        value: new Vector3(0, 0, 0) // Start from zero scale
      },
      {
        frame: 30 * duration, // Convert duration to frames
        value: originalScale // End at original scale
      }
    ];
    
    scaleAnim.setKeys(keyframes);
    scaleAnim.setEasingFunction(easing);
    
    // Add animation to mesh
    mesh.animations = [];
    mesh.animations.push(scaleAnim);
    
    // Start animation with delay
    setTimeout(() => {
      scene.beginAnimation(mesh, 0, 30 * duration, false);
      
      // If the mesh has skeleton animations, play them after scaling
      if (mesh.skeleton) {
        setTimeout(() => {
          scene.beginAnimation(mesh.skeleton, 0, 100, true);
        }, 100); // Small delay after scaling starts
      }
    }, delay * 1000); // Convert seconds to milliseconds
  });
}